import { useEffect, useRef, useState } from 'react';
import { Trans } from '../i18n/Trans';

/** 动作结果：成功 / 什么都没变（也要说！）/ 失败 */
export type ActionResult = { ok: true; msg?: string } | { ok: false; msg: string } | void;

/**
 * 带**就近文字反馈**的动作按钮。刷新、保存、复制这类"点一下就完事"的操作，
 * 光让数据悄悄变一下是不够的——用户不知道到底点没点上。
 *
 * 三条纪律（都是被用户当面指出来才补上的）：
 * 1. **反馈必须是文字**，就放在按钮里或按钮下方，不用顶部 toast：眼睛正看着这个按钮，
 *    反馈跑到屏幕另一头等于没有。
 * 2. **"什么都没变"也要说**。最容易漏的就是这种：一天只能刷一次汇率，同一天再点数据不变，
 *    不说话用户只会以为按钮坏了。动作返回 `{ok:true, msg:'今天已刷过…'}` 就能说清。
 * 3. **处理中要禁止重复点**，并且按钮文字变成进行时（"刷新中…"）。
 */
export function ActionButton({
  onRun, children, busyLabel, className = 'btn', doneLabel, holdMs = 2600, inline = false,
}: {
  onRun: () => Promise<ActionResult>;
  children: React.ReactNode;
  /** 进行中的文字，默认走 common.working */
  busyLabel?: string;
  className?: string;
  /** 成功但动作没给文案时显示什么，默认 common.done */
  doneLabel?: string;
  holdMs?: number;
  /** true = 反馈显示在按钮右边（一行放得下时），false = 按钮下方另起一行 */
  inline?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; msg: string } | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const show = (ok: boolean, msg: string) => {
    setNote({ ok, msg });
    window.clearTimeout(timer.current);
    // 失败的提示留久一点：用户要有时间读完，甚至复制
    timer.current = window.setTimeout(() => setNote(null), ok ? holdMs : holdMs * 3);
  };

  const run = async () => {
    if (busy) return;              // 处理中禁止重复触发
    setBusy(true);
    setNote(null);
    try {
      const r = await onRun();
      if (!r || r.ok) show(true, (r && r.msg) || doneLabel || Trans.t('common.done'));
      else show(false, r.msg);
    } catch (e) {
      show(false, e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className={'action-wrap' + (inline ? ' inline' : '')}>
      <button className={className} disabled={busy} onClick={run}>
        {busy ? (busyLabel || Trans.t('common.working')) : children}
      </button>
      {note && (
        <span className={'action-note' + (note.ok ? ' ok' : ' bad')} role="status" aria-live="polite">
          {note.msg}
        </span>
      )}
    </span>
  );
}
